import React, { useMemo, useState } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import Badge from "@react/shared/ui/Badge"
import PurchaseOrderModal from "./PurchaseOrderModal"
import PurchaseOrderShowModal from "./PurchaseOrderShowModal"
import { usePanorama } from "../context/PanoramaContext"
import { fmt, dateFmt } from "../helpers/format"
import { formatMoney } from "../helpers/poTotals"
import ActionButton from "./ActionButton"

const STATUS_TONE = {
  "In preparation":           "slate",
  "Price request":            "blue",
  "Confirmed":                "violet",
  "Goods partially received": "amber",
  "Goods fully received":     "emerald",
}

const StatTile = ({ label, value, tone }) => (
  <div className={`px-4 py-3 border rounded-xl ${tone || "bg-slate-50 border-slate-200"}`}>
    <div className="text-[10.5px] uppercase tracking-wider text-slate-500 font-medium">{label}</div>
    <div className="text-[18px] font-semibold tabular-nums text-slate-900">{value}</div>
  </div>
)

const EngagementsSection = ({ engagements: initial }) => {
  const { refresh, state } = usePanorama()
  const projectId = initial?.project_id
  const currency  = initial?.currency

  const [items, setItems]         = useState(initial?.items || [])
  const [total, setTotal]         = useState(initial?.total)
  const [paid]                    = useState(initial?.paid)
  const [remaining, setRemaining] = useState(initial?.remaining)
  const [modalOpen, setModalOpen] = useState(false)
  const [showId, setShowId]       = useState(null)

  const DRAFT_STATUSES = ["In preparation", "Price request"]
  const isCommitted = (item) => !DRAFT_STATUSES.includes(item.status)

  const upsert = (purchaseOrder) => {
    setItems((previous) => {
      const next = previous.some((item) => item.id === purchaseOrder.id)
        ? previous.map((item) => (item.id === purchaseOrder.id ? purchaseOrder : item))
        : [...previous, purchaseOrder]
      const totalCents     = next.filter(isCommitted).reduce((accumulator, item) => accumulator + (item.subtotal?.cents || 0), 0)
      const remainingCents = totalCents - (paid?.cents || 0)
      setTotal({ cents: totalCents, formatted: formatMoney(totalCents / 100, currency) })
      setRemaining({ cents: remainingCents, formatted: formatMoney(remainingCents / 100, currency) })
      return next
    })
    refresh()
  }

  const sortedEngagements = useMemo(
    () => [...items].sort((first, second) => String(first.number || "").localeCompare(String(second.number || ""))),
    [items]
  )

  return (
    <section id="section-engagements" className="px-7 scroll-mt-24">
      <header className="pl-[16px] mb-3 flex items-start justify-between gap-4">
        <div>
          <h2 className="text-[15px] font-semibold text-slate-900">{I18n.t("projects.engagements.title")}</h2>
          <p className="text-[12.5px] text-slate-500 mt-0.5">{I18n.t("projects.engagements.subtitle")}</p>
        </div>
        {initial?.can_create && (
          <ActionButton onClick={() => setModalOpen(true)}>
            {I18n.t("projects.engagements.actions.new")}
          </ActionButton>
        )}
      </header>

      <div className="grid gap-3 grid-cols-1 md:grid-cols-3 mb-4">
        <StatTile label={I18n.t("projects.engagements.summary.total")} value={fmt(total)} />
        <StatTile label={I18n.t("projects.engagements.summary.paid")} value={fmt(paid)} tone="bg-emerald-50/60 border-emerald-200" />
        <StatTile label={I18n.t("projects.engagements.summary.remaining")} value={fmt(remaining)} tone="bg-amber-50/60 border-amber-200" />
      </div>

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        <div className="grid grid-cols-[72px_minmax(0,2fr)_minmax(0,1.1fr)_minmax(0,1.4fr)_128px_140px_136px] gap-4 px-5 py-2.5 bg-slate-50 border-b border-slate-200 text-[10.5px] uppercase tracking-wider text-slate-500 font-medium">
          <div>{I18n.t("projects.engagements.table.number")}</div>
          <div>{I18n.t("projects.engagements.table.title")}</div>
          <div>{I18n.t("projects.engagements.table.lot")}</div>
          <div>{I18n.t("projects.engagements.table.supplier")}</div>
          <div>{I18n.t("projects.engagements.table.delivery")}</div>
          <div>{I18n.t("projects.engagements.table.status")}</div>
          <div className="text-right">{I18n.t("projects.engagements.table.amount")}</div>
        </div>

        {sortedEngagements.length === 0 ? (
          <div className="px-6 py-12 text-center text-[13px] text-slate-500">
            {I18n.t("projects.engagements.empty")}
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {sortedEngagements.map((engagement) => (
              <button
                key       = {engagement.id}
                type      = "button"
                onClick   = {() => setShowId(engagement.id)}
                className = "group w-full text-left grid grid-cols-[72px_minmax(0,2fr)_minmax(0,1.1fr)_minmax(0,1.4fr)_128px_140px_136px] gap-4 px-5 py-3 items-center text-[12.5px] hover:bg-slate-50/70 transition-colors"
              >
                <div className="font-mono text-[11.5px] text-slate-400">{engagement.number}</div>
                <div className="min-w-0 flex items-center gap-1.5">
                  <span className="text-slate-900 font-medium truncate" title={engagement.title}>{engagement.title || "—"}</span>
                  <FontAwesomeIcon icon="eye" className="w-2.5 h-2.5 text-slate-300 opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
                </div>
                <div className="min-w-0 text-slate-600 truncate" title={engagement.project_task?.title}>{engagement.project_task?.title || <span className="text-slate-400">{I18n.t("projects.engagements.form.lot_whole_project")}</span>}</div>
                <div className="min-w-0 text-slate-600 truncate" title={engagement.supplier?.name}>{engagement.supplier?.name || "—"}</div>
                <div className="text-slate-500 tabular-nums">{engagement.expected_delivery_date ? dateFmt(engagement.expected_delivery_date) : "—"}</div>
                <div>
                  <Badge tone={STATUS_TONE[engagement.status] || "neutral"} variant="soft" size="sm">
                    {engagement.status_label}
                  </Badge>
                </div>
                <div className="text-right tabular-nums text-slate-900 font-semibold">{fmt(engagement.subtotal)}</div>
              </button>
            ))}
          </div>
        )}
      </div>

      <PurchaseOrderModal
        isOpen           = {modalOpen}
        projectId        = {projectId}
        lots             = {initial?.lots || []}
        costCategories   = {state?.enabled_cost_categories || []}
        productSearchUrl = {initial?.product_search_url}
        onClose          = {() => setModalOpen(false)}
        onSaved          = {upsert}
      />

      <PurchaseOrderShowModal
        id      = {showId}
        isOpen  = {showId != null}
        onClose = {() => setShowId(null)}
      />
    </section>
  )
}

export default EngagementsSection
